import { Theme } from "@mui/material";
import { makeStyles } from "@mui/styles";

export const useStyles = makeStyles((theme: Theme) => ({
  modal: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%) rotate(0deg)",
    display: "flex",
    flexDirection: "column",
    width: 600,
    maxHeight: "calc(100vh - 48px)",
    boxSizing: "border-box",
    borderRadius: 6,
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: theme.palette.divider,
    opacity: 1,
    overflow: "hidden",
    backgroundColor: theme.palette.background.paper,
    boxShadow: "0 4px 24px rgba(37, 56, 88, 0.12)",
    outline: "none",
    direction: "rtl",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexShrink: 0,
    height: 56,
    gap: 10,
    padding: "16px 24px",
    boxSizing: "border-box",
    borderTopLeftRadius: 6,
    borderTopRightRadius: 6,
    backgroundColor: theme.palette.grey[400],
  },
  title: {
    fontFamily: theme.typography.fontFamily,
    fontSize: 16,
    fontWeight: 600,
    lineHeight: "24px",
    color: theme.palette.secondary.main,
    textAlign: "right",
    flex: 1,
    margin: 0,
  },
  closeButton: {
    flexShrink: 0,
    padding: 0,
    width: 24,
    height: 24,
    color: theme.palette.text.primary,
    "& .MuiSvgIcon-root": { fontSize: 20 },
  },
  body: {
    display: "flex",
    flexDirection: "column",
    gap: 24,
    padding: 24,
    overflowY: "auto",
    backgroundColor: theme.palette.background.paper,
  },
  topRow: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) 336px",
    gap: 24,
    alignItems: "stretch",
  },
  splitRow: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 2.2fr) minmax(0, 1fr)",
    gap: 24,
    alignItems: "start",
  },
  selectStack: {
    display: "flex",
    flexDirection: "column",
    gap: 24,
    width: 336,
    minWidth: 0,
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
  },
  imageField: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    minWidth: 0,
    minHeight: 0,
  },
  label: {
    fontFamily: theme.typography.fontFamily,
    fontWeight: 500,
    fontSize: 12,
    lineHeight: "140%",
    textAlign: "right",
    color: theme.palette.text.primary,
    margin: 0,
  },
  uploadBox: {
    position: "relative",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    minHeight: 148,
    padding: 12,
    boxSizing: "border-box",
    borderRadius: 4,
    border: `1px dashed ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    cursor: "pointer",
    overflow: "hidden",
    "&:hover": {
      borderColor: theme.palette.primary.main,
    },
  },
  uploadBoxError: {
    borderColor: theme.palette.error.main,
  },
  uploadIcon: {
    fontSize: 28,
    color: theme.palette.text.disabled,
  },
  uploadText: {
    fontFamily: theme.typography.fontFamily,
    fontWeight: 400,
    fontSize: 14,
    lineHeight: "21px",
    color: theme.palette.secondary.main,
    textAlign: "center",
    margin: 0,
  },
  uploadHint: {
    fontFamily: theme.typography.fontFamily,
    fontWeight: 400,
    fontSize: 10,
    lineHeight: "140%",
    color: theme.palette.text.disabled,
    textAlign: "center",
    margin: 0,
    maxWidth: 210,
  },
  uploadError: {
    fontFamily: theme.typography.fontFamily,
    fontWeight: 400,
    fontSize: 11,
    lineHeight: "140%",
    color: theme.palette.error.main,
    textAlign: "right",
    margin: 0,
  },
  uploadPreview: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    borderRadius: 4,
  },
  removeImageButton: {
    position: "absolute",
    top: 6,
    left: 6,
    padding: 2,
    width: 22,
    height: 22,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    boxShadow: "0 1px 4px rgba(37, 56, 88, 0.16)",
    "&:hover": {
      backgroundColor: theme.palette.background.paper,
    },
    "& .MuiSvgIcon-root": { fontSize: 16 },
  },
  input: {
    width: "100%",
    maxWidth: "100%",
    height: 48,
    margin: 0,
    opacity: 1,
    transform: "rotate(0deg)",
    "& .MuiOutlinedInput-root": {
      width: "100%",
      height: 48,
      boxSizing: "border-box",
      borderRadius: 4,
      borderWidth: 1,
      gap: 10,
      opacity: 1,
      transform: "rotate(0deg)",
      backgroundColor: theme.palette.background.paper,
      fontFamily: theme.typography.fontFamily,
      fontWeight: 400,
      fontSize: 14,
      lineHeight: "21px",
      color: theme.palette.secondary.main,
      "& fieldset": {
        borderColor: theme.palette.divider,
        borderWidth: 1,
      },
      "&:hover fieldset": { borderColor: theme.palette.divider },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
        borderWidth: 1,
      },
      "&.Mui-disabled": {
        backgroundColor: theme.palette.background.paper,
        "& fieldset": { borderColor: theme.palette.divider },
      },
    },
    "& .MuiOutlinedInput-input": {
      height: "100%",
      boxSizing: "border-box",
      paddingTop: 12,
      paddingRight: 8,
      paddingBottom: 12,
      paddingLeft: 16,
      fontFamily: theme.typography.fontFamily,
      fontWeight: 400,
      fontSize: 14,
      lineHeight: "21px",
      textAlign: "right",
      color: theme.palette.secondary.main,
      "&::placeholder": {
        color: theme.palette.text.disabled,
        opacity: 1,
      },
      "&.Mui-disabled": {
        color: theme.palette.secondary.main,
        WebkitTextFillColor: theme.palette.secondary.main,
      },
    },
    "& .MuiSelect-select": {
      display: "flex",
      alignItems: "center",
      height: 48,
      minHeight: "48px !important",
      boxSizing: "border-box",
      paddingTop: "12px !important",
      paddingRight: "8px !important",
      paddingBottom: "12px !important",
      paddingLeft: "16px !important",
      fontFamily: theme.typography.fontFamily,
      fontWeight: 400,
      fontSize: 14,
      lineHeight: "21px",
      textAlign: "right",
      color: theme.palette.secondary.main,
      "&.Mui-disabled": {
        color: theme.palette.secondary.main,
        WebkitTextFillColor: theme.palette.secondary.main,
      },
    },
    "& .MuiSelect-icon": {
      right: "auto",
      left: 8,
      top: "50%",
      transform: "translateY(-50%) rotate(0deg)",
      position: "absolute",
    },
    "& .MuiSelect-icon.Mui-disabled": {
      color: theme.palette.text.secondary,
    },
  },
  placeholder: {
    color: theme.palette.text.disabled,
    WebkitTextFillColor: theme.palette.text.disabled,
  },
  selectMenuPaper: {
    maxHeight: 240,
    marginTop: 4,
    borderRadius: 4,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "0 4px 24px rgba(37, 56, 88, 0.12)",
    boxSizing: "border-box",
    overflowY: "auto",
    overflowX: "hidden",
  },
  selectMenuList: {
    padding: 4,
    boxSizing: "border-box",
  },
  selectMenuItem: {
    fontFamily: theme.typography.fontFamily,
    fontSize: 14,
    lineHeight: "21px",
    fontWeight: 400,
    textAlign: "right",
    color: theme.palette.secondary.main,
    minHeight: 40,
    borderRadius: 4,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    "&.Mui-selected": {
      backgroundColor: theme.palette.action.selected,
    },
    "&.Mui-selected:hover": {
      backgroundColor: theme.palette.action.selected,
    },
  },
  footer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    flexShrink: 0,
    height: 68,
    gap: 12,
    padding: "16px 24px",
    boxSizing: "border-box",
    borderBottomRightRadius: 6,
    borderBottomLeftRadius: 6,
    backgroundColor: theme.palette.grey[400],
  },
  cancelButton: {
    height: 36,
    padding: "15px 28px",
    borderRadius: 4,
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: theme.palette.text.primary,
    color: theme.palette.text.primary,
    fontFamily: theme.typography.fontFamily,
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1,
    textTransform: "none",
    backgroundColor: "transparent",
    boxShadow: "none",
    "&:hover": {
      borderColor: theme.palette.text.primary,
      backgroundColor: "transparent",
      boxShadow: "none",
    },
  },
  addButton: {
    height: 36,
    padding: "15px 28px",
    borderRadius: 4,
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: theme.palette.primary.main,
    fontFamily: theme.typography.fontFamily,
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1,
    textTransform: "none",
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: theme.palette.primary.main,
      boxShadow: "none",
    },
    "&.Mui-disabled": {
      backgroundColor: `${theme.palette.divider} !important`,
      borderColor: theme.palette.divider,
      color: `${theme.palette.text.disabled} !important`,
    },
  },
}));
